/**
 * The record view of a registrar course (`/admin/courses/{courseId}`): the
 * section is its own editor. Section, Teaching team, and When and where it
 * meets edit each fact where it is shown, through one save engine and one
 * version for the whole record; Publication holds the lifecycle act. The
 * course plan and materials follow full width.
 *
 * The shared roster completes setup here and also has its own full view.
 * Attendance, coursework, and grades remain their own views of this record.
 *
 * The CRN is never a field: publication issues it server-side, which is why
 * publication is registrar-owned.
 */
import { useId, type ReactNode } from "react";

import { CoursePlan } from "@/components/courses/CoursePlan";
import { ClassroomReferencesTile } from "@/components/courses/ClassroomReferencesTile";
import { CourseMaterialsPanel } from "@/components/courses/CourseMaterialsPanel";
import { useCourseFormSources } from "@/components/courses/use-course-form-sources";
import { useTenantConfig } from "@/hooks/use-tenant-config";
import { useAuth } from "@/contexts/AuthContext";
import { Bento } from "@/components/ui/box";
import type { Box } from "@/components/ui/box.schema";
import type { Course } from "@/lib/api/courses";
import type { Read } from "@/components/ui/box.schema";
import { isHistoricalTerm } from "@/lib/term-state";

import { RegistrarCourseRoster } from "./RegistrarCourseRoster";
import { sectionFactsBox, sectionMeetingBox } from "./section-fact-models";
import { useTeachingTeamModels } from "./teaching-team-models";
import { usePublicationModels } from "./publication-models";
import { useSectionSave } from "./use-section-save";

/**
 * A titled group for a composed panel that lays its own tiles. Tiles compose a
 * page and never nest, so the group is a plain region over them.
 */
function RecordGroup({
  eyebrow,
  title,
  description,
  className,
  children,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  className?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section aria-labelledby={id} className={className ?? "grid min-w-0 content-start gap-3"}>
      <div className="grid min-w-0 gap-1">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h3 id={id} className="text-section font-semibold text-foreground">{title}</h3>
        {description && <p className="max-w-2xl text-ui text-muted-foreground">{description}</p>}
      </div>
      {children}
    </section>
  );
}

function useRecordBoxes(course: Course | null, recordId: string, read: Read<Course>, onDiscarded?: (course: Course) => void) {
  const { me } = useAuth();
  const sources = useCourseFormSources({ canWriteRegistrarFields: true });
  const engine = useSectionSave(course, me ? { tenant: me.tenant_id, user: me.user_id } : null);
  const props = { course, recordId, engine, sources, read, onDiscarded };
  const team = useTeachingTeamModels(props);
  const publication = usePublicationModels(props);
  return [sectionFactsBox(props), ...team, sectionMeetingBox(props), ...publication] as Box[];
}

/** The authorized record is not read yet. Draw its own read boxes, without guessed values. */
export function RegistrarCourseRecordRead({ recordId, read }: { recordId: string; read: Read<Course> }) {
  const boxes = useRecordBoxes(null, recordId, read);
  return <Bento id={`course-record-${recordId}`} journey="registrar-course-record" boxes={boxes} />;
}

export function RegistrarCourseRecord({ course, read, onDiscarded, discarded = false }: { course: Course; listHref: string; read?: Read<Course>; onDiscarded?: (course: Course) => void; discarded?: boolean }) {
  const { sessionPortalEnabled } = useTenantConfig();
  const historical = isHistoricalTerm(course.semester_status);
  const boxes = useRecordBoxes(course, course.id, read ?? { status: "ready", value: course, readAt: course.updated_at }, onDiscarded);

  return (
    <div className="grid min-w-0 gap-[var(--layout-bento-gap)] xl:grid-cols-2 xl:items-start">
      <Bento id={`course-record-${course.id}`} journey="registrar-course-record" boxes={discarded ? boxes.filter(box => box.id === `discard-${course.id}`) : boxes} className="xl:col-span-2" />

      {!discarded && <>
      <div className="min-w-0 xl:col-span-2">
        <RegistrarCourseRoster course={course} presentation="setup" />
      </div>

      <RecordGroup
        title="Course plan"
        description="Each session in calendar order and the plan it carries. Open a session to read its plan."
        className="grid min-w-0 content-start gap-3 xl:col-span-2"
      >
        <CoursePlan
          preparation={false}
          courseId={course.id}
          // Migration 061 keeps every section's zone, defaulting to this one.
          timezone={course.timezone ?? "America/New_York"}
          termOpen={!historical}
          canEdit={false}
          currentUnitId={null}
          // Registrar oversight, so "Open session" is the admin teaching
          // shell's. The route grants nothing: an admin with no assignment on
          // this course is refused by the projection.
          shell="adminTeaching"
          portalEnabled={sessionPortalEnabled}
          headingLevel="h3"
          // A calendar address is for a seat on the course; oversight holds none.
          calendar={false}
        />
      </RecordGroup>

      <RecordGroup
        title="Materials"
        className="grid min-w-0 content-start gap-3 xl:col-span-2"
      >
        <CourseMaterialsPanel courseId={course.id} canEdit={course.can_teach === true} timezone={course.timezone} />
      </RecordGroup>

      {/* Classroom's stream, read-only and apart from IMAM materials. Absent when the section has no Classroom link. */}
      <ClassroomReferencesTile
        courseId={course.id}
        canRefresh
        timezone={course.timezone ?? null}
        className="xl:col-span-2"
      />
      </>}
    </div>
  );
}
